import classNames from "classnames";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { mergeRefs } from "react-merge-refs";
import classes from "./Dropdown.module.scss";
const Select = React.forwardRef(
  (
    {
      name,
      list,
      onSelect,
      value,
      onChange,
      label,
      required,
      fullWidth,
      ignoreOffsetTop,
      flat,
      placeholder,
      defaultValue,
      startAdornment,
      endAdornment,
      containerClassName,
      interactivity = true,
      disabled = false,
      error = null,
      onBlur,
    },
    ref
  ) => {
    const [showList, setShowList] = useState(false);
    const localRef = useRef();
    const selectRef = mergeRefs([localRef, ref]);
    const handleSelectItem = (e) => {
      e.stopPropagation();
      e.preventDefault();
      setShowList(false);
      const value = e.target.getAttribute("data-value");
      localRef.current.value = value;
      onBlur && onBlur();
      onSelect && onSelect(value);
      onChange && onChange({ target: { value, name } });
    };

    const openDropdownList = (e) => {
      e.stopPropagation();
      e.preventDefault();
      setShowList(!showList);
    };

    const prevent = (e) => {
      e.stopPropagation();
      e.preventDefault();
    };

    useEffect(() => {
      if (typeof defaultValue !== "undefined" || typeof value !== "undefined") {
        localRef.current.value = value || defaultValue;
        onSelect && onSelect(value || defaultValue);
      }
    }, [defaultValue, value]); //eslint-disable-line
    return (
      <div
        onClick={prevent}
        className={classNames(
          `d-flex flex-column`,
          classes["container"],
          containerClassName,
          {
            "w-100": fullWidth,
            "w-auto": !fullWidth,
            "mt-0": ignoreOffsetTop,
            [classes["disabled"]]: disabled,
          }
        )}
        data-testid={"container"}
      >
        {label && (
          <div
            className={`${classes["label-text"]} ${
              required && classes["required"]
            }`}
            onClick={() => setShowList(!showList)}
          >
            {label}
          </div>
        )}
        <label
          className={classNames(classes["wrapper"], {
            [classes["interactivity"]]: interactivity,
            "w-100": fullWidth,
            [classes["error"]]: !!error,
          })}
          data-testid={"dropdown-wrapper"}
          onClick={!disabled ? openDropdownList : undefined}
        >
          {startAdornment && (
            <span className={classes["startAdornment"]}>{startAdornment}</span>
          )}
          <select
            ref={selectRef}
            name={name}
            className={`${classes["dropdown"]} ${
              flat ? classes["no-shadow"] : ""
            }`}
            onClick={!disabled ? openDropdownList : undefined}
            required={required || false}
            defaultValue={defaultValue || ""}
          >
            <option value="" hidden>
              {placeholder || "Select your option"}
            </option>
            {list.map((item) => (
              <option value={item.value} key={item.id} disabled={item.disabled}>
                {item.title}
              </option>
            ))}
          </select>
          <span
            role={"button"}
            className={`${showList ? "icon-up-dir" : "icon-down-dir"} ${
              classes["icon"]
            }`}
            onClick={() => setShowList(!showList)}
          />
          {endAdornment && (
            <span className={classes["endAdornment"]}>{endAdornment}</span>
          )}
          <ul className={`${showList && classes["show-list"]}`}>
            {list.map((item) => (
              <li
                data-value={item.value}
                key={item.id}
                onClick={handleSelectItem}
                className={`${
                  localRef.current?.value === item.value && classes["selected"]
                }`}
                disabled={item.disabled}
              >
                {item.title}
              </li>
            ))}
          </ul>
        </label>
        {error && (
          <span className={classNames(classes["error-text"])}>{error}</span>
        )}
      </div>
    );
  }
);

Select.propTypes = {
  /**
   * Label to be shown above the dropdown
   */
  label: PropTypes.string,
  /**
   * provide a name to access the value inside a form
   */
  name: PropTypes.string,
  /**
   * this will render options
   */
  list: PropTypes.arrayOf(PropTypes.object),
  /**
   * callback function which fires after selecting an option
   */
  onSelect: PropTypes.func,
  /**
   * callback function which fires after selecting an option
   * same as onSelect, it will be used for form validation comparability
   */
  onChange: PropTypes.func,
  /**
   * marks dropdown as required
   */
  required: PropTypes.bool,
  /**
   * set true to have a width of 100%
   */
  fullWidth: PropTypes.bool,
  /**
   * set true to ignore margin top of a dropdown
   */
  ignoreOffsetTop: PropTypes.bool,
  /**
   * set true to ignore dropdown box shadow
   */
  flat: PropTypes.bool,
  /**
   * default text to be shown in the select box
   */
  placeholder: PropTypes.string,
  //TODO: write tests for defaultValue
  /**
   * default value of the dropdown
   */
  defaultValue: PropTypes.string,
  /**
   * default value of the dropdown
   */
  value: PropTypes.string,
};

Select.defaultProps = {
  list: [],
  onSelect: undefined,
};

export const Dropdown = React.memo(Select);
